import type { VocabularyEntry, VocabularyCategory, VocabularyEntityType } from '@/db/Types'

export interface ParsedToken {
	text: string
	vocabularyEntry: VocabularyEntry | null
	category: VocabularyCategory | null
	entityType: VocabularyEntityType | null
	entityId: string | null
	isResolved: boolean
}

export interface ParseResult {
	tokens: ParsedToken[]
	unresolvedTokens: string[]
	canonicalOrder: ParsedToken[]
}

// Canonical ordering priority (lower = first)
const CATEGORY_PRIORITY: Record<string, number> = {
	formation: 0,
	modifier: 1,
	motion: 2,
	blocking: 3,
	route: 4,
	protection: 5,
}

export class PlayCallParser {
	/**
	 * Parse a full play call string against vocabulary.
	 * Uses greedy longest-match: tries longest sequences first.
	 * Case-insensitive matching.
	 */
	parse(input: string, vocabulary: VocabularyEntry[]): ParseResult {
		const trimmed = input.trim()
		if (!trimmed) {
			return { tokens: [], unresolvedTokens: [], canonicalOrder: [] }
		}

		// Normalize: collapse multiple spaces to single
		const normalized = trimmed.replace(/\s+/g, ' ')
		const words = normalized.split(' ')

		// Build a lookup map: lowercase code -> VocabularyEntry
		const entriesByCode = new Map<string, VocabularyEntry>()
		for (const entry of vocabulary) {
			const key = entry.code.toLowerCase()
			// First entry wins (in case of duplicates)
			if (!entriesByCode.has(key)) {
				entriesByCode.set(key, entry)
			}
		}

		const tokens: ParsedToken[] = []
		let pos = 0

		while (pos < words.length) {
			let matched = false

			// Try longest match first (from remaining words to just 1 word)
			for (let len = words.length - pos; len >= 1; len--) {
				const candidate = words.slice(pos, pos + len).join(' ')
				const entry = entriesByCode.get(candidate.toLowerCase())

				if (entry) {
					tokens.push({
						text: candidate,
						vocabularyEntry: entry,
						category: entry.category,
						entityType: entry.entity_type,
						entityId: entry.entity_id,
						isResolved: true,
					})
					pos += len
					matched = true
					break
				}
			}

			if (!matched) {
				const word = words[pos]!
				tokens.push({
					text: word,
					vocabularyEntry: null,
					category: null,
					entityType: null,
					entityId: null,
					isResolved: false,
				})
				pos++
			}
		}

		const unresolvedTokens = tokens.filter(t => !t.isResolved).map(t => t.text)

		// Canonical ordering: sort by category priority, unresolved last
		const canonicalOrder = [...tokens].sort((a, b) => {
			const priorityA = a.isResolved && a.category ? (CATEGORY_PRIORITY[a.category] ?? 99) : 100
			const priorityB = b.isResolved && b.category ? (CATEGORY_PRIORITY[b.category] ?? 99) : 100
			return priorityA - priorityB
		})

		return { tokens, unresolvedTokens, canonicalOrder }
	}

	/**
	 * Suggest matching vocabulary entries for a partial token.
	 * Case-insensitive prefix matching, sorted by relevance.
	 */
	suggest(prefix: string, vocabulary: VocabularyEntry[]): VocabularyEntry[] {
		const trimmed = prefix.trim().toLowerCase()
		if (!trimmed) return vocabulary.slice(0, 10)

		return vocabulary
			.filter(entry => entry.code.toLowerCase().startsWith(trimmed))
			.sort((a, b) => {
				// Exact match first
				const aExact = a.code.toLowerCase() === trimmed
				const bExact = b.code.toLowerCase() === trimmed
				if (aExact && !bExact) return -1
				if (!aExact && bExact) return 1
				// Shorter codes first
				if (a.code.length !== b.code.length) return a.code.length - b.code.length
				// Alphabetical
				return a.code.localeCompare(b.code)
			})
			.slice(0, 10)
	}
}
